import React from 'react';
import T from 'i18n-react';
import type { NextPage, NextPageContext } from 'next';

import MetaTags from 'Components/MetaTags/MetaTags';
import PageComposer from 'Components/PageComposer/PageComposer';
import StaticPages from 'Components/StaticPages/StaticPages';
import texts from 'Components/texts.json';
import { PAGE_SLUGS } from 'Utilities/consts';
import { getSlug, isSSR } from 'Utilities/helpers';
import { isStaticPageSlug } from 'Utilities/staticPageSlugs';

import { ErrorPage } from './_error.page';

T.setTexts(texts);

type MetaConfig = {
	description?: string;
	noIndex?: boolean;
	noIndexFollow?: boolean;
	title?: string;
};

const metaConfig = {
	'activate-account': {
		description: `${T.translate('metaTags.pages.activateAccount.description')}`,
		title: `${T.translate('metaTags.pages.activateAccount.title')}`,
	},
	'cancellation-and-price-adjustments': {
		description: `${T.translate('metaTags.pages.cancellationAndPriceAdjustments.description')}`,
		title: `${T.translate('metaTags.pages.cancellationAndPriceAdjustments.title')}`,
	},
	'code-of-conduct': {
		description: `${T.translate('metaTags.pages.codeOfConduct.description')}`,
		title: `${T.translate('metaTags.pages.codeOfConduct.title')}`,
	},
	'contact-us': {
		description: `${T.translate('metaTags.pages.contactUs.description')}`,
		noIndexFollow: true,
		title: `${T.translate('metaTags.pages.contactUs.title')}`,
	},
	cookies: {
		description: `${T.translate('metaTags.pages.cookies.description')}`,
		title: `${T.translate('metaTags.pages.cookies.title')}`,
	},
	'payment-terms': {
		description: `${T.translate('metaTags.pages.paymentTerms.description')}`,
		title: `${T.translate('metaTags.pages.paymentTerms.title')}`,
	},
	'payments-assurance-policy': {
		description: `${T.translate('metaTags.pages.paymentsAssurancePolicy.description')}`,
		title: `${T.translate('metaTags.pages.paymentsAssurancePolicy.title')}`,
	},
	privacy: {
		description: `${T.translate('metaTags.pages.privacy.description')}`,
		title: `${T.translate('metaTags.pages.privacy.title')}`,
	},
	terms: {
		description: `${T.translate('metaTags.pages.terms.description')}`,
		title: `${T.translate('metaTags.pages.terms.title')}`,
	},
};

const getMetaPropsFromConfig = (metaProps: MetaConfig) => {
	const { description = '', noIndex = false, noIndexFollow = false, title = '' } = metaProps;
	return { description, noIndex, noIndexFollow, ogDescription: description, ogTitle: title, title };
};

export const NextjsStaticPage: NextPage<{
	fullWidth?: boolean;
	pageUrl: string;
	query?: { signUpSidebar?: boolean };
}> = (props) => {
	const { pageUrl, query: { signUpSidebar = true } = {} } = props;
	const slug = getSlug(pageUrl);

	if (!isStaticPageSlug(slug)) {
		return <ErrorPage />;
	}

	const metaProps = getMetaPropsFromConfig(metaConfig[slug]);

	return (
		<>
			<MetaTags {...metaProps} />
			<PageComposer pageSlug={PAGE_SLUGS.STATIC_PAGE}>
				<StaticPages signUpSidebar={signUpSidebar} slug={slug} {...props} />
			</PageComposer>
		</>
	);
};

NextjsStaticPage.getInitialProps = (context: NextPageContext) => {
	/**
	 * Next.js v13 for server side rendering has removed asPath showing the original URL from the browser.
	 * https://github.com/vercel/next.js/issues/56879
	 * So we need to use originalUrl from req object to get the original URL.
	 */
	if (isSSR()) {
		return {
			pageUrl: context.req?.originalUrl || '',
			query: context.req?.query || {},
		};
	}

	return { pageUrl: context.asPath || '', query: context.query };
};

export default NextjsStaticPage;
